/**
 * Fixed, code-defined symbol system for the `line` field type.
 *
 * Two halves:
 *  - {@link LINE_SYMBOL_SHAPES} — the reusable base SHAPES (stroke/marker/label rendering). A line
 *    type's `symbolId` references one of these; the shapes carry NO color.
 *  - {@link DEFAULT_LINE_TYPES} — the preset line-type library seeded onto every new line field
 *    (`{ symbolId, symbolColor, title }`). Color lives here, per line type, not on the shape.
 *
 * The shape ids are a published cross-app contract: IKE Field (UFO-129) resolves a line's selected
 * type → its `symbolId` → a shape to render the polyline identically. Additive changes only.
 * A line type is identified by its (unique) `title`; a row is a preset iff its title matches a
 * {@link DEFAULT_LINE_TYPES} entry (so there is no stored "custom" flag — it is derived).
 */

export type LineSymbolGroup = 'general' | 'existing_conditions' | 'existing_utilities';

export type LineSymbolStroke = 'solid' | 'dash' | 'dotted';

/** Repeating glyph drawn along the stroke for symbolic shapes (fence/railroad). */
export type LineSymbolMarker = 'x' | 'tick';

/** A base line shape: stroke pattern + optional glyph/label. Color is applied per line type. */
export interface LineSymbolShape {
	/** Stable id — the published cross-app contract value. */
	id: string;
	name: string;
	/** Stroke weight in px. */
	weight: number;
	stroke: LineSymbolStroke;
	/** SVG stroke-dasharray (only meaningful when stroke === 'dash'). */
	dash?: number[];
	marker?: LineSymbolMarker;
	/** Repeating mid-line letter label (utility codes, e.g. 'E', 'GAS'). */
	label?: string;
}

/** A fully resolved, renderable symbol: a shape plus its color. */
export interface LineSymbol extends LineSymbolShape {
	/** Stroke color (HTML hex). */
	color: string;
}

/** Default color for a line type with no explicit `symbolColor` (e.g. a freshly added custom type). */
export const DEFAULT_LINE_SYMBOL_COLOR = '#202124';

/** Default shape selected when a designer adds a custom line type. */
export const DEFAULT_LINE_SYMBOL_ID = 'solid';

/** The fixed set of base shapes offered in the shape picker. */
export const LINE_SYMBOL_SHAPES: readonly LineSymbolShape[] = [
	{ id: 'solid', name: 'Solid', stroke: 'solid', weight: 2 },
	{ id: 'dotted', name: 'Dotted', stroke: 'dotted', weight: 2.6 },
	{ id: 'dashed', name: 'Dashed', stroke: 'dash', weight: 2, dash: [12, 7] },
	{ id: 'fineDashed', name: 'Fine dashed', stroke: 'dash', weight: 2, dash: [4, 3] },
	{ id: 'longDashed', name: 'Long dashed', stroke: 'dash', weight: 2, dash: [18, 8] },
	{ id: 'dashDotted', name: 'Dash-dotted', stroke: 'dash', weight: 2, dash: [10, 3, 2, 3] },
	{ id: 'dashDotDotted', name: 'Dash-dot-dotted', stroke: 'dash', weight: 2, dash: [15, 5, 3, 5] },
	{ id: 'fence', name: 'Fence', stroke: 'solid', weight: 1.6, marker: 'x' },
	{ id: 'railroad', name: 'Railroad', stroke: 'solid', weight: 1.6, marker: 'tick' },
	{ id: 'catv', name: 'CATV (TV)', stroke: 'solid', weight: 1.8, label: 'TV' },
	{ id: 'electric', name: 'Electric (E)', stroke: 'solid', weight: 1.8, label: 'E' },
	{ id: 'fiber', name: 'Fiber (FO)', stroke: 'solid', weight: 1.8, label: 'FO' },
	{ id: 'gas', name: 'Gas (GAS)', stroke: 'solid', weight: 1.8, label: 'GAS' },
	{ id: 'sewer', name: 'Sewer (SS)', stroke: 'solid', weight: 1.8, label: 'SS' },
	{ id: 'storm', name: 'Storm (SD)', stroke: 'solid', weight: 1.8, label: 'SD' },
	{ id: 'telephone', name: 'Telephone (T)', stroke: 'solid', weight: 1.8, label: 'T' },
	{ id: 'water', name: 'Water (W)', stroke: 'solid', weight: 1.8, label: 'W' },
];

export const LINE_SYMBOL_SHAPE_BY_ID: Readonly<Record<string, LineSymbolShape>> = LINE_SYMBOL_SHAPES.reduce<
	Record<string, LineSymbolShape>
>((acc, shape) => {
	acc[shape.id] = shape;
	return acc;
}, {});

export interface DefaultLineType {
	symbolId: string;
	symbolColor: string;
	title: string;
	group: LineSymbolGroup;
}

/** The preset line-type library seeded onto every new line field (in display order). */
export const DEFAULT_LINE_TYPES: readonly DefaultLineType[] = [
	{ symbolId: 'dotted', symbolColor: '#202124', title: 'Generic Line', group: 'general' },

	{ symbolId: 'solid', symbolColor: '#8a5a2b', title: 'Ground Profile', group: 'existing_conditions' },
	{ symbolId: 'dashDotted', symbolColor: '#455a64', title: 'Edge of Curb', group: 'existing_conditions' },
	{ symbolId: 'solid', symbolColor: '#3c4043', title: 'Edge of Pavement', group: 'existing_conditions' },
	{ symbolId: 'fineDashed', symbolColor: '#6b7075', title: 'Edge of Sidewalk', group: 'existing_conditions' },
	{ symbolId: 'dashed', symbolColor: '#2f6fe0', title: 'Curb & Gutter (C&G)', group: 'existing_conditions' },
	{ symbolId: 'solid', symbolColor: '#8a8f94', title: 'Lane Striping', group: 'existing_conditions' },
	{ symbolId: 'longDashed', symbolColor: '#202124', title: 'Centerline', group: 'existing_conditions' },
	{ symbolId: 'dashDotDotted', symbolColor: '#3c4043', title: 'Right of Way', group: 'existing_conditions' },
	{ symbolId: 'fence', symbolColor: '#202124', title: 'Fence', group: 'existing_conditions' },
	{ symbolId: 'railroad', symbolColor: '#202124', title: 'Railroad', group: 'existing_conditions' },
	{ symbolId: 'dashDotted', symbolColor: '#2a9fd6', title: 'Waterway', group: 'existing_conditions' },
	{ symbolId: 'solid', symbolColor: '#e01e1e', title: 'Proposed Bore', group: 'existing_conditions' },
	{ symbolId: 'dashed', symbolColor: '#e01e1e', title: 'Proposed Trench', group: 'existing_conditions' },

	{ symbolId: 'catv', symbolColor: '#e8821e', title: 'CATV', group: 'existing_utilities' },
	{ symbolId: 'electric', symbolColor: '#e01e1e', title: 'Electric', group: 'existing_utilities' },
	{ symbolId: 'fiber', symbolColor: '#e8821e', title: 'Fiber Optic', group: 'existing_utilities' },
	{ symbolId: 'gas', symbolColor: '#a89400', title: 'Gas', group: 'existing_utilities' },
	{ symbolId: 'sewer', symbolColor: '#1fa11f', title: 'Sanitary Sewer', group: 'existing_utilities' },
	{ symbolId: 'storm', symbolColor: '#d61fbf', title: 'Storm Drain', group: 'existing_utilities' },
	{ symbolId: 'telephone', symbolColor: '#e8821e', title: 'Telephone', group: 'existing_utilities' },
	{ symbolId: 'water', symbolColor: '#2f6fe0', title: 'Water', group: 'existing_utilities' },
];

const DEFAULT_LINE_TYPE_BY_TITLE: Readonly<Record<string, DefaultLineType>> = DEFAULT_LINE_TYPES.reduce<
	Record<string, DefaultLineType>
>((acc, type) => {
	acc[type.title] = type;
	return acc;
}, {});

export const LINE_SYMBOL_GROUP_ORDER: readonly LineSymbolGroup[] = [
	'general',
	'existing_conditions',
	'existing_utilities',
];

export const LINE_SYMBOL_GROUP_LABEL: Readonly<Record<LineSymbolGroup, string>> = {
	general: 'General',
	existing_conditions: 'Conditions',
	existing_utilities: 'Utilities',
};

export function resolveLineSymbol(ref: { symbolId: string; symbolColor?: string | null }): LineSymbol | null {
	const shape = LINE_SYMBOL_SHAPE_BY_ID[ref.symbolId];
	if (shape == null) {
		return null;
	}
	const color = ref.symbolColor != null && ref.symbolColor !== '' ? ref.symbolColor : DEFAULT_LINE_SYMBOL_COLOR;
	return { ...shape, color };
}

/** The fallback symbol when a line type's `symbolId` is unknown (e.g. a removed shape). */
export const DEFAULT_LINE_SYMBOL: LineSymbol = resolveLineSymbol({ symbolId: DEFAULT_LINE_SYMBOL_ID })!;

/** Whether a title belongs to the preset library — i.e. it is a default (non-custom) line type. */
export function isDefaultLineTitle(title: string): boolean {
	return DEFAULT_LINE_TYPE_BY_TITLE[title] != null;
}

/** The picker group for a line type, resolved by title; custom titles fall under `general`. */
export function lineTypeGroup(title: string): LineSymbolGroup {
	return DEFAULT_LINE_TYPE_BY_TITLE[title]?.group ?? 'general';
}

/**
 * Whether a shape's stroke pattern is freely selectable. A "fixed" shape carries an intrinsic glyph —
 * a utility code label (E, GAS, …) or a marker (fence/railroad) — so only its color may be customized.
 */
export function isStylableLineSymbol(symbolId: string): boolean {
	const shape = LINE_SYMBOL_SHAPE_BY_ID[symbolId];
	return shape != null && shape.label == null && shape.marker == null;
}

/** The base shapes a designer may freely apply as a stroke pattern (no fixed code label or marker). */
export const STYLABLE_LINE_SYMBOL_SHAPES: readonly LineSymbolShape[] = LINE_SYMBOL_SHAPES.filter(
	(shape) => shape.label == null && shape.marker == null
);

/**
 * Curated quick-pick colors for the symbol customizer — a hand-picked, well-spread swatch set. Not
 * exhaustive: the customizer also offers a native picker for any other color, so a preset whose color
 * is not listed here still opens with that color selected (shown as the editable custom dot).
 */
export const LINE_SYMBOL_COLOR_PALETTE: readonly string[] = [
	'#202124',
	'#6b7075',
	'#8a5a2b',
	'#e01e1e',
	'#e8821e',
	'#a89400',
	'#1fa11f',
	'#1b9e91',
	'#2a9fd6',
	'#2f6fe0',
	'#7a5af8',
	'#d61fbf',
];
